import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Project, ProjectTemplate } from '@claude-code-api/shared';
import {
  Badge,
  Card,
  CardContent,
  Button,
} from '@/components/ui';
import { StatusIndicator } from '@/components/ui/status-indicator';
import {
  FolderGit2,
  FileCode,
  Folder,
  Check,
  Copy,
  Database,
} from 'lucide-react';

interface ProjectOverviewTabProps {
  project: Project;
  template: ProjectTemplate | undefined;
  sessionsCount: number;
  activeSessionId: string | undefined;
}

export function ProjectOverviewTab({
  project,
  template,
  sessionsCount,
  activeSessionId,
}: ProjectOverviewTabProps) {
  const { t, i18n } = useTranslation('projects');
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat(i18n.language, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));
  };

  return (
    <div className="space-y-6">
      {/* Project Info Card */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {/* Info Grid */}
          <div className="grid grid-cols-4 divide-x">
            <div className="p-5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('overview.type')}</p>
              <div className="mt-2 flex items-center gap-2">
                {template ? (
                  <>
                    <Badge variant="secondary" className="bg-primary/10 text-primary">{t('overview.template')}</Badge>
                    <span className="text-sm">{template.name}</span>
                  </>
                ) : project.gitUrl ? (
                  <>
                    <Badge variant="secondary">{t('overview.github')}</Badge>
                    <span className="text-sm">{t('overview.repository')}</span>
                  </>
                ) : (
                  <Badge variant="secondary">{t('overview.blankProject')}</Badge>
                )}
              </div>
            </div>
            <div className="p-5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('overview.database')}</p>
              <div className="mt-2">
                {project.hasDatabase ? (
                  <Badge className="gap-1 bg-success/10 text-success hover:bg-success/10">
                    <Check className="w-3 h-3" />
                    {t('overview.postgresActive')}
                  </Badge>
                ) : (
                  <span className="text-sm text-muted-foreground">{t('overview.notConfigured')}</span>
                )}
              </div>
            </div>
            <div className="p-5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('overview.sessions')}</p>
              <div className="mt-2 flex items-center gap-3">
                <span className="text-lg font-heading font-semibold">{sessionsCount}</span>
                {activeSessionId && (
                  <StatusIndicator status="running" size="md" showLabel label={t('overview.runningCount', { count: 1 })} />
                )}
              </div>
            </div>
            <div className="p-5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('overview.projectId')}</p>
              <p className="mt-2 text-sm font-mono text-muted-foreground truncate" title={project.id}>
                {project.id}
              </p>
            </div>
          </div>

          {/* Git URL */}
          {project.gitUrl && (
            <div className="px-6 py-4 bg-muted/30 border-t">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FolderGit2 className="w-5 h-5 text-muted-foreground" />
                  <a
                    href={project.gitUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline font-mono"
                >
                  {project.gitUrl}
                </a>
                </div>
                <Button
                  variant={copiedField === 'gitUrl' ? 'secondary' : 'outline'}
                  size="sm"
                  onClick={() => copyToClipboard(project.gitUrl!, 'gitUrl')}
                  className={copiedField === 'gitUrl' ? 'bg-success/10 text-success' : ''}
                >
                  {copiedField === 'gitUrl'
                    ? <><Check className="w-3 h-3 mr-1" /> {t('overview.copied')}</>
                    : <><Copy className="w-3 h-3 mr-1" /> {t('overview.copy')}</>}
                </Button>
              </div>
            </div>
          )}

          {/* Forgejo Repo URL */}
          {project.forgejoRepoUrl && !project.gitUrl && (
            <div className="px-6 py-4 bg-muted/30 border-t">
              <div className="flex items-center gap-3">
                <FolderGit2 className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm font-mono text-muted-foreground">
                  {project.forgejoRepoUrl}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              {project.gitUrl ? (
                <FolderGit2 className="w-4 h-4 text-primary" />
              ) : project.templateId ? (
                <FileCode className="w-4 h-4 text-primary" />
              ) : (
                <Folder className="w-4 h-4 text-primary" />
              )}
            </div>
            <span className="text-sm font-medium">{t('overview.projectType')}</span>
          </div>
          <p className="text-sm text-muted-foreground">
            {template ? template.name : project.gitUrl ? t('overview.githubImport') : t('overview.blankProject')}
          </p>
        </div>
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-success/10 flex items-center justify-center">
              <Database className="w-4 h-4 text-success" />
            </div>
            <span className="text-sm font-medium">{t('overview.database')}</span>
          </div>
          <p className="text-sm text-muted-foreground">
            {project.hasDatabase ? t('overview.connected') : t('overview.none')}
          </p>
        </div>
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
              <FolderGit2 className="w-4 h-4 text-accent" />
            </div>
            <span className="text-sm font-medium">{t('overview.cicd')}</span>
          </div>
          <p className="text-sm text-muted-foreground">
            {project.hasForgejoRepo ? t('overview.enabled') : t('overview.na')}
          </p>
        </div>
      </div>

      {/* Created date */}
      <p className="text-sm text-muted-foreground">
        {t('detail.created', { date: formatDate(project.createdAt) })}
      </p>
    </div>
  );
}
